/**
 * AppLayout — root layout: sidebar + content area.
 * Wires feature-specific components (UserMenu) into layout slots.
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Sidebar } from './Sidebar';
import { ContentArea } from './ContentArea';
import { UserMenu } from '@/features/auth/components/UserMenu';
import { FamilyViewToggle } from '@/features/auth/components/FamilyViewToggle';
import { BackgroundJobsIndicator } from '@/features/background-tasks/components/BackgroundJobsIndicator';
import { NotificationCenter } from '@/features/notifications/components/NotificationCenter';
import {
  dismissNotification,
  type NotificationsState,
} from '@/features/notifications/ducks';
import { navigate as routerNavigate } from '@/infra/navigation/history';
import type { Notification } from '@/infra/types/domain';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const close = React.useCallback(() => setMobileOpen(false), []);
  const dispatch = useDispatch();
  // Toast surface — render only ephemeral notifications (those with an
  // explicit auto_dismiss_ms set by an in-app dispatcher). The wider
  // notifications slice also collects server-pushed durable items
  // (Carried forward, due-date reminders, etc.) which belong in a
  // proper notifications panel, NOT cascaded as toasts. Without this
  // filter they pile up across sessions because they never get marked
  // 'read' by an auto-dismiss timer.
  const notifications = useSelector<{ notifications: NotificationsState }, Notification[]>(
    (s) => s.notifications.items.filter(
      (n) => (n.auto_dismiss_ms ?? 0) > 0 && n.status !== 'dismissed',
    ),
  );
  const onDismiss = React.useCallback(
    (id: string) => dispatch(dismissNotification(id)),
    [dispatch],
  );
  const onAction = React.useCallback((n: Notification) => {
    if (n.action_url) routerNavigate(n.action_url);
    dispatch(dismissNotification(n.id));
  }, [dispatch]);

  React.useEffect(() => {
    if (!isMobile && mobileOpen) setMobileOpen(false);
  }, [isMobile, mobileOpen]);

  const open = isMobile ? mobileOpen : true;

  return (
    <div className="app-layout">
      <Sidebar open={open} onNavigate={close} />
      {isMobile && mobileOpen && (
        <div
          className="sidebar-backdrop"
          aria-hidden="true"
          onClick={close}
          data-testid="sidebar-backdrop"
        />
      )}
      <ContentArea
        leadingSlot={
          <button
            type="button"
            className="sidebar-toggle"
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileOpen}
            aria-controls="main-navigation"
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span aria-hidden="true">☰</span>
          </button>
        }
        headerSlot={
          <>
            <FamilyViewToggle />
            <BackgroundJobsIndicator />
            <UserMenu />
          </>
        }
      >
        {children}
      </ContentArea>
      <NotificationCenter
        notifications={notifications}
        onDismiss={onDismiss}
        onAction={onAction}
      />
    </div>
  );
}
